import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Calendar, Loader2, Image as ImageIcon, Upload, X, 
  MapPin, Clock, Music, Sparkles, Eye, EyeOff,
  Info, CheckCircle2, FolderSync, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { eventsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import CloudinaryUploader from '../../components/CloudinaryUploader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_318b698c-05e0-45f0-aa51-d3d773434be0/artifacts/p0qxibr4_BETO%20FISCHER%20%282%29.svg";

const EVENT_STATUSES = [
  { value: 'draft', label: 'Borrador', description: 'Solo visible para admins', icon: EyeOff, color: 'text-zinc-400' },
  { value: 'published', label: 'Publicado', description: 'Visible para todos', icon: Eye, color: 'text-green-400' },
  { value: 'private', label: 'Privado', description: 'Solo con link directo', icon: Users, color: 'text-blue-400' },
];

const IMAGE_POSITIONS = [
  { value: 'top', label: 'Superior' },
  { value: 'center', label: 'Centro' },
  { value: 'bottom', label: 'Inferior' }
];

const CreateEventPage = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { user, hasPermission } = useAuth();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isEditing = !!eventId;
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    address: '',
    artists: '',
    image_url: '',
    image_position: 'center',
    status: 'draft',
    drive_folder_id: ''
  });

  const [errors, setErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isEditing && user) {
      loadEvent();
    }
  }, [isEditing, user]);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    setLoading(false);
  };

  const loadEvent = async () => {
    try {
      const response = await eventsApi.getById(eventId);
      const event = response.data;
      
      // Parse date and time separately
      const eventDate = new Date(event.date);
      
      setFormData({
        title: event.title || '',
        description: event.description || '',
        date: format(eventDate, 'yyyy-MM-dd'),
        time: format(eventDate, 'HH:mm'),
        location: event.location || '',
        address: event.address || '',
        artists: (event.artists || []).join(', '),
        image_url: event.image_url || '',
        image_position: event.image_position || 'center',
        status: event.status || 'draft',
        drive_folder_id: event.drive_folder_id || ''
      });
    } catch (error) {
      console.error('Error loading event:', error);
      toast.error('Error al cargar el evento');
      navigate('/admin/eventos');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) newErrors.title = 'El título es requerido';
    if (!formData.description.trim()) newErrors.description = 'La descripción es requerida';
    if (!formData.date) newErrors.date = 'La fecha es requerida';
    if (!formData.time) newErrors.time = 'La hora es requerida';
    if (!formData.location.trim()) newErrors.location = 'El lugar es requerido';
    if (!formData.address.trim()) newErrors.address = 'La dirección es requerida';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor corrige los errores del formulario');
      return;
    }

    setSaving(true);
    try {
      // Combine date and time
      const dateTime = new Date(`${formData.date}T${formData.time}`);
      
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        date: dateTime.toISOString(),
        location: formData.location.trim(),
        address: formData.address.trim(),
        artists: formData.artists.split(',').map(a => a.trim()).filter(Boolean),
        ticket_price: 0, // Se configura en Tandas
        service_fee: 0,  // Se configura en Tandas
        ticket_quantity: 0, // Se configura en Tandas
        image_url: formData.image_url || null,
        image_position: formData.image_position || 'center',
        status: formData.status,
        drive_folder_id: formData.drive_folder_id || null
      };

      if (isEditing) {
        await eventsApi.update(eventId, payload);
        toast.success('Evento actualizado exitosamente');
      } else {
        await eventsApi.create(payload);
        toast.success('Evento creado exitosamente');
      }

      navigate('/admin/eventos');
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el evento');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Drag and drop handlers for image
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      await handleImageUpload(file);
    } else {
      toast.error('Solo se permiten archivos de imagen');
    }
  };

  const handleImageUpload = async (file) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      handleChange('image_url', reader.result);
    };
    reader.readAsDataURL(file);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#27272A]">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link 
                to="/admin/eventos" 
                className="p-2 hover:bg-[#27272A] rounded-lg transition-colors"
              >
                <ArrowLeft size={20} className="text-[#A1A1AA]" />
              </Link>
              <img 
                src={LOGO_URL}
                alt="Bravo" 
                className="h-10 w-auto"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/eventos')}
                className="border-[#27272A] hover:bg-[#27272A]"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="bg-[#D4AF37] hover:bg-[#B8972E] text-black font-semibold"
                data-testid="save-event-btn"
              >
                {saving ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={18} className="mr-2" />
                    {isEditing ? 'Guardar Cambios' : 'Crear Evento'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Page Title */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 flex items-center justify-center">
                <Calendar size={24} className="text-[#D4AF37]" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-black uppercase tracking-tight">
                  {isEditing ? 'Editar Evento' : 'Nuevo Evento'}
                </h1>
                <p className="text-[#A1A1AA] text-sm">
                  {isEditing ? 'Modifica los detalles del evento' : 'Completa los detalles para crear un nuevo evento'}
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Section: Basic Info */}
            <section className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
              <h2 className="text-lg font-bold uppercase tracking-wide mb-6 flex items-center gap-2">
                <Sparkles size={18} className="text-[#D4AF37]" />
                Información Básica
              </h2>
              
              <div className="space-y-6">
                {/* Title */}
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#71717A] mb-2 block">
                    Título del Evento *
                  </Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="Ej: Noche de Jazz en vivo"
                    className={`bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] rounded-lg h-12 text-lg ${errors.title ? 'border-red-500' : ''}`}
                    data-testid="event-title-input"
                  />
                  {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
                </div>

                {/* Description */}
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#71717A] mb-2 block">
                    Descripción *
                  </Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Describe tu evento: qué pueden esperar los asistentes, ambiente, música, etc."
                    className={`bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] rounded-lg min-h-[120px] resize-none ${errors.description ? 'border-red-500' : ''}`}
                    data-testid="event-description-input"
                  />
                  {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
                </div>

                {/* Artists */}
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#71717A] mb-2 block">
                    <Music size={14} className="inline mr-1" />
                    Artistas / DJs
                  </Label>
                  <Input
                    value={formData.artists}
                    onChange={(e) => handleChange('artists', e.target.value)}
                    placeholder="Artista 1, DJ 2, Banda 3 (separados por coma)"
                    className="bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] rounded-lg"
                    data-testid="event-artists-input"
                  />
                  <p className="text-[#52525B] text-xs mt-1">Separa múltiples artistas con comas</p>
                </div>
              </div>
            </section>

            {/* Section: Date & Time */}
            <section className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
              <h2 className="text-lg font-bold uppercase tracking-wide mb-6 flex items-center gap-2">
                <Clock size={18} className="text-[#D4AF37]" />
                Fecha y Horario
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Date */}
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#71717A] mb-2 block">
                    Fecha del Evento *
                  </Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleChange('date', e.target.value)}
                    className={`bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] rounded-lg h-12 ${errors.date ? 'border-red-500' : ''}`}
                    data-testid="event-date-input"
                  />
                  {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
                </div>

                {/* Time */}
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#71717A] mb-2 block">
                    Hora de Inicio *
                  </Label>
                  <Input
                    type="time"
                    value={formData.time}
                    onChange={(e) => handleChange('time', e.target.value)}
                    className={`bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] rounded-lg h-12 ${errors.time ? 'border-red-500' : ''}`}
                    data-testid="event-time-input"
                  />
                  {errors.time && <p className="text-red-500 text-xs mt-1">{errors.time}</p>}
                </div>
              </div>
            </section>

            {/* Section: Location */}
            <section className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
              <h2 className="text-lg font-bold uppercase tracking-wide mb-6 flex items-center gap-2">
                <MapPin size={18} className="text-[#D4AF37]" />
                Ubicación
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Venue Name */}
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#71717A] mb-2 block">
                    Nombre del Lugar *
                  </Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    placeholder="Ej: Club Nocturno XYZ"
                    className={`bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] rounded-lg h-12 ${errors.location ? 'border-red-500' : ''}`}
                    data-testid="event-location-input"
                  />
                  {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location}</p>}
                </div>

                {/* Full Address */}
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#71717A] mb-2 block">
                    Dirección Completa *
                  </Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    placeholder="Ej: Av. Corrientes 1234, CABA"
                    className={`bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] rounded-lg h-12 ${errors.address ? 'border-red-500' : ''}`}
                    data-testid="event-address-input"
                  />
                  {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                </div>
              </div>
            </section>

            {/* Section: Event Image */}
            <section className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
              <h2 className="text-lg font-bold uppercase tracking-wide mb-6 flex items-center gap-2">
                <ImageIcon size={18} className="text-[#D4AF37]" />
                Imagen del Evento
              </h2>
              
              <div className="space-y-6">
                {/* Image Preview */}
                {formData.image_url ? (
                  <div className="relative rounded-xl overflow-hidden border border-[#27272A]">
                    <img 
                      src={formData.image_url} 
                      alt="Preview" 
                      className="w-full h-64 object-cover"
                      style={{ objectPosition: formData.image_position }}
                    />
                    <button
                      type="button"
                      onClick={() => handleChange('image_url', '')}
                      className="absolute top-3 right-3 w-8 h-8 bg-red-500/80 hover:bg-red-500 text-white rounded-full flex items-center justify-center transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div
                    className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
                      isDragging 
                        ? 'border-[#D4AF37] bg-[#D4AF37]/5' 
                        : 'border-[#27272A] hover:border-[#52525B]'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <Upload size={48} className="mx-auto mb-4 text-[#52525B]" />
                    <p className="text-[#A1A1AA] mb-2">
                      Arrastra una imagen aquí o
                    </p>
                    <CloudinaryUploader
                      folder="bravo/events"
                      resourceType="image"
                      accept="image/*"
                      buttonText="Seleccionar archivo"
                      showPreview={false}
                      onUploadComplete={(result) => {
                        handleChange('image_url', result.url);
                      }}
                    />
                    <p className="text-[#52525B] text-xs mt-4">
                      Formatos: JPG, PNG, WebP • Máximo 5MB • Recomendado: 1920x1080px
                    </p>
                  </div>
                )}

                {/* Manual URL Input */}
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#71717A] mb-2 block">
                    O pegar URL de imagen
                  </Label>
                  <Input
                    value={formData.image_url}
                    onChange={(e) => handleChange('image_url', e.target.value)}
                    placeholder="https://..."
                    className="bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] rounded-lg"
                    data-testid="event-image-url-input"
                  />
                </div>

                {/* Image Position */}
                {formData.image_url && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#71717A] mb-2 block">
                      Posición de la Imagen
                    </Label>
                    <div className="flex gap-2">
                      {IMAGE_POSITIONS.map((pos) => (
                        <button
                          key={pos.value}
                          type="button"
                          onClick={() => handleChange('image_position', pos.value)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            formData.image_position === pos.value
                              ? 'bg-[#D4AF37] text-black'
                              : 'bg-[#27272A] text-[#A1A1AA] hover:bg-[#3F3F46]'
                          }`}
                        >
                          {pos.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Section: Status & Settings */}
            <section className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
              <h2 className="text-lg font-bold uppercase tracking-wide mb-6 flex items-center gap-2">
                <Eye size={18} className="text-[#D4AF37]" />
                Estado y Configuración
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Status */}
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#71717A] mb-2 block">
                    Estado del Evento
                  </Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(value) => handleChange('status', value)}
                  >
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] rounded-lg h-12" data-testid="event-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border border-[#27272A]">
                      {EVENT_STATUSES.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          <div className="flex items-center gap-2">
                            <status.icon size={16} className={status.color} />
                            <span>{status.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[#52525B] text-xs mt-1">
                    {EVENT_STATUSES.find(s => s.value === formData.status)?.description}
                  </p>
                </div>

                {/* Google Drive Folder */}
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#71717A] mb-2 block">
                    <FolderSync size={14} className="inline mr-1" />
                    ID Carpeta Google Drive
                  </Label>
                  <Input
                    value={formData.drive_folder_id}
                    onChange={(e) => handleChange('drive_folder_id', e.target.value)}
                    placeholder="Ej: 1abc123def456..."
                    className="bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] rounded-lg h-12"
                    data-testid="event-drive-folder-input"
                  />
                  <p className="text-[#52525B] text-xs mt-1">
                    Para sincronizar fotos del evento con la galería
                  </p>
                </div>
              </div>
            </section>

            {/* Info Banner */}
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-xl p-4 flex items-start gap-3">
              <Info size={20} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[#D4AF37] font-medium text-sm">
                  {isEditing ? 'Los cambios se aplicarán inmediatamente' : 'Después de crear el evento'}
                </p>
                <p className="text-[#A1A1AA] text-xs mt-1">
                  {isEditing 
                    ? 'Los usuarios verán las actualizaciones en tiempo real.' 
                    : 'Podrás configurar tandas de tickets, códigos de descuento y más desde el panel del evento.'}
                </p>
              </div>
            </div>

            {/* Submit Button (Mobile) */}
            <div className="md:hidden">
              <Button
                type="submit"
                disabled={saving}
                className="w-full bg-[#D4AF37] hover:bg-[#B8972E] text-black font-semibold h-12"
              >
                {saving ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={18} className="mr-2" />
                    {isEditing ? 'Guardar Cambios' : 'Crear Evento'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </main>
    </div>
  );
};

export default CreateEventPage;
